import { useCallback, useEffect, useId, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { apiGet } from '../utils/apiClient';
import { useAuth } from '../context/AuthContext';
import { BillingToggle } from './BillingToggle';
import { PlanErrorSummary } from './PlanErrorSummary';
import { UpgradeConfirmDrawer } from './UpgradeConfirmDrawer';
import { markTrialInterstitialAcknowledged } from './TrialExpiredInterstitial';
import { clearCachedProfile } from './PrivateRoute';
import { PLAN_DETAILS, formatBillingDate, formatPrice, getPlanPrice, trialDaysLeft } from '../utils/planPricing';
import {
  billingErrorMessage,
  changePlan,
  changeTrialPlan,
  openBillingPortal,
  refreshBilling,
  resumeSubscription,
  startCheckout,
} from '../utils/planActions';
import { PlanButton, PlanCard } from './PlanCard';

// Plan & billing (docs/PLAN_SELECTION_SPEC.md §2b), reached from More, the trial-expired screen
// and the expired banner. One screen, driven by /user/me + /households/me:
//   trial | expired | solo | family  — the four designed states
//   trial-subscribed — on the free trial having already subscribed in Stripe: the chosen plan
//                      starts when the trial ends, and can be changed for free until then
//   member — in someone else's household: their plan is the owner's, nothing to change here
//   free   — access granted by hand (tier 'free', or billing 'free'): nothing to pay or change
//   none   — no plan yet; owners who can still start a trial go to the plan screen instead
// Rule: never offer the plan + interval they are already on. Upgrades are immediate; downgrades
// and interval switches happen at renewal. Every payment action is a stub until Stripe (§7).

function getBillingState(profile, household, uid) {
  if (household?.ownerUid && uid && household.ownerUid !== uid) return 'member';
  const tier = (profile?.subscriptionTier || '').toLowerCase();
  if (tier === 'trial') return profile?.subscriptionPlan ? 'trial-subscribed' : 'trial';
  if (tier === 'expired') return tier;
  if (tier === 'solo' || tier === 'family') return profile?.subscriptionBilling === 'free' ? 'free' : tier;
  if (tier === 'free') return 'free';
  return 'none';
}

// The plan options to show for the toggle's interval, never including the current plan.
function getOptions(state, currentInterval, interval, currentPlan) {
  if (state === 'trial-subscribed') {
    return ['solo', 'family']
      .filter((plan) => !(plan === currentPlan && interval === currentInterval))
      .map((plan) => ({ plan, kind: 'trial-change' }));
  }
  if (state === 'trial' || state === 'expired' || state === 'none') {
    return [{ plan: 'solo', kind: 'subscribe' }, { plan: 'family', kind: 'subscribe' }];
  }
  if (state === 'solo') {
    // Decision C: from yearly Solo the only upgrade is yearly Family (see the note on the screen).
    const options = currentInterval === 'yearly' && interval === 'monthly' ? [] : [{ plan: 'family', kind: 'upgrade' }];
    if (interval !== currentInterval) options.push({ plan: 'solo', kind: 'switch-interval' });
    return options;
  }
  if (state === 'family') {
    const options = [];
    if (interval !== currentInterval) options.push({ plan: 'family', kind: 'switch-interval' });
    options.push({ plan: 'solo', kind: 'downgrade' });
    return options;
  }
  return [];
}

const perInterval = (interval) => (interval === 'yearly' ? 'year' : 'month');
const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

function YourPlanCard({ headingId, badge, badgeTone = 'current', title, children }) {
  const badgeClass = badgeTone === 'ended'
    ? 'bg-[var(--plan-notice-bg)] text-[color:var(--plan-notice-text)]'
    : 'bg-[var(--plan-current-badge-bg)] text-[color:var(--plan-heading-text)]';
  return (
    <section
      aria-labelledby={headingId}
      className="rounded-3xl border-2 border-[color:var(--plan-current-border)] bg-white p-6 sm:p-8"
    >
      <div className="flex items-start justify-between gap-4">
        <h2 id={headingId} className="text-base font-semibold text-[color:var(--plan-muted-text)]">
          Your plan
        </h2>
        {badge && <p className={`rounded-full px-3 py-1 text-sm font-bold ${badgeClass}`}>{badge}</p>}
      </div>
      <p className="mt-2 text-3xl font-bold text-[color:var(--plan-heading-text)]">{title}</p>
      <div className="mt-3 space-y-3 text-base text-[color:var(--plan-heading-text)]">{children}</div>
    </section>
  );
}

function LinkButton({ onClick, tone = 'olive', className = '', children }) {
  const color = tone === 'dark'
    ? 'text-[color:var(--plan-heading-text)] font-medium'
    : 'text-[color:var(--plan-solo-text)] font-semibold hover:text-[color:var(--plan-heading-text)]';
  return (
    <button
      type="button"
      onClick={onClick}
      className={`mpg-focus-ring inline-flex min-h-[44px] items-center rounded-md text-base underline underline-offset-4 ${color} ${className}`}
    >
      {children}
    </button>
  );
}

export function Plans() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [household, setHousehold] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [actionError, setActionError] = useState('');
  const [billingInterval, setBillingInterval] = useState(null);
  const [busyKey, setBusyKey] = useState('');
  const [statusKey, setStatusKey] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  // The interval being upgraded to while the confirm drawer is open, else null.
  const [upgradeInterval, setUpgradeInterval] = useState(null);
  // Back from Stripe Checkout without paying (its "back" link adds ?checkout=cancelled).
  const [searchParams] = useSearchParams();
  const [showCancelled, setShowCancelled] = useState(searchParams.get('checkout') === 'cancelled');
  // Back from Stripe's customer portal (it returns to /plans?from=portal): whatever was done there
  // (cancelled, kept the plan, new card) is fetched from Stripe straight away.
  const [fromPortal] = useState(searchParams.get('from') === 'portal');
  const planHeadingId = useId();
  const optionsHeadingId = useId();

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const loadProfile = fromPortal
        ? refreshBilling().then((result) => {
          clearCachedProfile();
          return result.profile;
        }).catch(() => apiGet('/user/me')) // a refresh problem just means the webhook catches up
        : apiGet('/user/me');
      const [me, hh] = await Promise.all([
        loadProfile,
        apiGet('/households/me').catch(() => null), // household details are a nice-to-have here
      ]);
      setProfile(me);
      setHousehold(hh || null);
    } catch (err) {
      console.warn('Failed to load plan and billing details:', err);
      setLoadError("We couldn't load your plan details. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [fromPortal]);

  useEffect(() => {
    load();
  }, [load]);

  const goBack = () => {
    // Opened directly (no in-app history): fall back to More, where Plan & billing lives.
    if (location.key === 'default') navigate('/More');
    else navigate(-1);
  };

  const header = (
    <header className="sticky top-0 z-10 border-b border-[color:var(--plan-card-border)] bg-white">
      <div className="mx-auto flex max-w-3xl items-center gap-2 px-4 py-3">
        <button
          type="button"
          onClick={goBack}
          aria-label="Back"
          className="mpg-focus-ring flex h-11 w-11 items-center justify-center rounded-full hover:bg-[var(--plan-page-bg)]"
        >
          <ChevronLeft className="h-6 w-6 text-[color:var(--plan-heading-text)]" aria-hidden="true" />
        </button>
        <h1 className="text-xl font-bold text-[color:var(--plan-heading-text)]">Plan &amp; billing</h1>
      </div>
    </header>
  );

  if (loading || loadError) {
    return (
      <div className="mpg-focus-scope min-h-screen bg-[var(--plan-page-bg)]">
        {header}
        <main className="mx-auto max-w-3xl px-4 py-6">
          {loading ? (
            <p role="status" className="text-base text-[color:var(--plan-muted-text)]">Loading your plan…</p>
          ) : (
            <>
              <PlanErrorSummary message={loadError} title="Couldn't load your plan" />
              <LinkButton onClick={load}>Try again</LinkButton>
            </>
          )}
        </main>
      </div>
    );
  }

  const state = getBillingState(profile, household, user?.uid);
  // An owner who can still start a trial belongs on the plan screen, which offers it.
  if (state === 'none' && profile?.needsPlanChoice) return <Navigate to="/onboarding/plan" replace />;

  // From the Stripe subscription. Plans set by hand have no interval and are treated as monthly.
  const currentInterval = profile?.subscriptionInterval === 'yearly' ? 'yearly' : 'monthly';
  // The plan they're on, or (trial-subscribed) the one that starts when the trial ends.
  const currentPlan = profile?.subscriptionPlan || (state === 'solo' || state === 'family' ? state : null);
  const interval = billingInterval
    || (state === 'solo' || state === 'family' || state === 'trial-subscribed' ? currentInterval : null)
    || (location.state?.selectedPlan === 'yearly' ? 'yearly' : 'monthly');

  const trialEnd = profile?.trialEndDate ? new Date(profile.trialEndDate) : null;
  const trialEndText = formatBillingDate(trialEnd);
  const renewalText = formatBillingDate(profile?.subscriptionRenewalDate);
  const onRenewal = renewalText ? ` on ${renewalText}` : '';
  // Set once a paid plan is cancelled: it runs until this date and does not renew.
  const accessUntilText = formatBillingDate(profile?.subscriptionEndDate);
  const otherMembers = (household?.members || []).filter((m) => m?.uid && m.uid !== household?.ownerUid).length;
  const owner = (household?.members || []).find((m) => m?.uid === household?.ownerUid);
  const ownerFirstName = String(owner?.name || '').trim().split(/\s+/)[0] || '';

  const changeInterval = (next) => {
    setBillingInterval(next);
    setStatusKey('');
  };

  const runAction = async (key, action) => {
    setActionError('');
    setStatusKey('');
    setSuccessMessage('');
    setShowCancelled(false);
    setBusyKey(key);
    let redirecting = false;
    try {
      const result = await action();
      redirecting = result?.status === 'redirecting';
      if (result?.status === 'coming-soon') setStatusKey(key);
    } catch (err) {
      console.warn('Plan & billing action failed:', err);
      setActionError(billingErrorMessage(err, 'Something went wrong and nothing was changed. Please try again.'));
    } finally {
      // Leaving for Stripe: keep the button busy until the page changes.
      if (!redirecting) setBusyKey('');
    }
  };

  // Free plan change during the trial. The chosen option disappears once it is the current plan,
  // so the confirmation goes in the page-level success box rather than on the card.
  const switchTrialPlan = async (plan, nextInterval) => {
    const { profile: updated } = await changeTrialPlan({ plan, interval: nextInterval });
    clearCachedProfile();
    setProfile(updated);
    setBillingInterval(null);
    const endText = formatBillingDate(updated?.trialEndDate ? new Date(updated.trialEndDate) : null);
    setSuccessMessage(
      `Done. Your ${PLAN_DETAILS[plan].name} ${nextInterval} plan starts${endText ? ` on ${endText}` : ' when your trial ends'}. Nothing is charged until then.`
    );
    return { status: 'changed' };
  };

  // "Keep my plan": undo a pending cancellation.
  const keepPlan = async () => {
    const { profile: updated } = await resumeSubscription();
    clearCachedProfile();
    setProfile(updated);
    const onTrial = (updated?.subscriptionTier || '').toLowerCase() === 'trial';
    const planName = PLAN_DETAILS[updated?.subscriptionPlan]?.name || 'Your';
    const date = formatBillingDate(onTrial ? (updated?.trialEndDate ? new Date(updated.trialEndDate) : null) : updated?.subscriptionRenewalDate);
    setSuccessMessage(onTrial
      ? `Done. Your plan is no longer cancelled. Your ${planName} plan starts${date ? ` on ${date}` : ' when your trial ends'}.`
      : `Done. Your plan is no longer cancelled${date ? ` and renews on ${date}` : ''}.`);
    return { status: 'kept' };
  };

  const finishUpgrade = (updated) => {
    setUpgradeInterval(null);
    clearCachedProfile();
    if (updated) setProfile(updated);
    setBillingInterval(null);
    setActionError('');
    const renewal = formatBillingDate(updated?.subscriptionRenewalDate);
    setSuccessMessage(`Done. You're now on Family${renewal ? `, renewing on ${renewal}` : ''}. You can invite household members from Manage household members.`);
  };

  const cancelled = profile?.subscriptionCancelled === true;
  const keepPlanButton = (
    <div>
      <PlanButton variant="secondary" busy={busyKey === 'keep'} onClick={() => runAction('keep', keepPlan)}>
        Keep my plan
      </PlanButton>
    </div>
  );
  const manageBillingLink = (
    <LinkButton className="text-left" onClick={() => runAction('portal', () => openBillingPortal({ flow: 'manage' }))}>
      Manage payment method and invoices
    </LinkButton>
  );

  // ---- "Your plan" card ---------------------------------------------------------------------
  let yourPlan;
  if (state === 'trial') {
    const daysLeft = trialEnd ? trialDaysLeft(trialEnd) : null;
    const left = daysLeft == null ? '' : daysLeft <= 0 ? 'Ends today. ' : `${plural(daysLeft, 'day')} left, ends ${trialEndText}. `;
    yourPlan = (
      <YourPlanCard headingId={planHeadingId} badge="Free trial" title="Free trial">
        <p>{left}You have full access to every feature.</p>
      </YourPlanCard>
    );
  } else if (state === 'trial-subscribed') {
    const daysLeft = trialEnd ? trialDaysLeft(trialEnd) : null;
    const left = daysLeft == null ? '' : daysLeft <= 0 ? 'Ends today. ' : `${plural(daysLeft, 'day')} left, ends ${trialEndText}. `;
    const planName = PLAN_DETAILS[currentPlan]?.name || 'Your';
    const price = currentPlan ? formatPrice(getPlanPrice(currentPlan, currentInterval)) : '';
    yourPlan = (
      <YourPlanCard headingId={planHeadingId} badge="Free trial" title="Free trial">
        <p>{left}You have full access to every feature.</p>
        {cancelled ? (
          <p>
            Cancelled: your {planName} plan won&apos;t start, and you won&apos;t be charged. Your trial ends
            {trialEndText ? ` on ${trialEndText}` : ' as planned'}.
          </p>
        ) : (
          <p>
            Then your {planName} plan starts: {price} per {perInterval(currentInterval)}, first charge
            {trialEndText ? ` on ${trialEndText}` : ' when your trial ends'}.
          </p>
        )}
        {cancelled && keepPlanButton}
        {manageBillingLink}
      </YourPlanCard>
    );
  } else if (state === 'expired') {
    yourPlan = (
      <YourPlanCard headingId={planHeadingId} badge="Trial ended" badgeTone="ended" title="Free trial ended">
        {trialEndText && <p>Your trial finished on {trialEndText}.</p>}
        <p className="rounded-2xl bg-[var(--plan-notice-bg)] p-4 text-[color:var(--plan-notice-text)]">
          Your recipes and meal plans are safe. You can still view and export them, but planning is paused
          until you choose a plan.
        </p>
      </YourPlanCard>
    );
  } else if (state === 'solo' || state === 'family') {
    const price = formatPrice(getPlanPrice(state, currentInterval));
    yourPlan = (
      <YourPlanCard headingId={planHeadingId} badge="Current plan" title={PLAN_DETAILS[state].name}>
        <p>
          {price} per {perInterval(currentInterval)}.
          {accessUntilText
            ? ` Cancelled: your plan ends on ${accessUntilText} and won't renew.`
            : (renewalText ? ` Renews on ${renewalText}.` : '')}
        </p>
        {state === 'family' && (
          <div>
            <p>Household: {otherMembers ? `you and ${plural(otherMembers, 'member')}` : 'just you for now'}.</p>
            {/* A real link: it goes to another screen rather than performing an action here. */}
            <Link
              to="/family"
              className="mpg-focus-ring inline-flex min-h-[44px] items-center rounded-md text-base font-semibold text-[color:var(--plan-solo-text)] underline underline-offset-4 hover:text-[color:var(--plan-heading-text)]"
            >
              Manage household members
            </Link>
          </div>
        )}
        {cancelled && keepPlanButton}
        {manageBillingLink}
      </YourPlanCard>
    );
  } else if (state === 'member') {
    yourPlan = (
      <YourPlanCard headingId={planHeadingId} badge="Included" title="Family">
        <p>
          You're part of {ownerFirstName ? `${ownerFirstName}'s` : 'a'} household, so you're covered by their Family
          plan. Only {ownerFirstName || 'the household owner'} can change it.
        </p>
      </YourPlanCard>
    );
  } else if (state === 'free') {
    const tier = (profile?.subscriptionTier || '').toLowerCase();
    yourPlan = (
      <YourPlanCard headingId={planHeadingId} badge="Free access" title={PLAN_DETAILS[tier]?.name || 'Free access'}>
        <p>You've been given free access, so there's nothing to pay.</p>
      </YourPlanCard>
    );
  } else {
    yourPlan = (
      <YourPlanCard headingId={planHeadingId} title="No plan yet">
        <p>Choose a plan below to get started.</p>
      </YourPlanCard>
    );
  }

  // ---- Option cards -------------------------------------------------------------------------
  // While a cancellation is pending nothing else is offered: keeping the plan comes first.
  const options = cancelled ? [] : getOptions(state, currentInterval, interval, currentPlan);
  const optionsHeading = {
    trial: 'Choose a plan',
    'trial-subscribed': 'Change your plan',
    expired: 'Choose a plan to keep planning',
    none: 'Choose a plan',
    solo: 'Change your plan',
    family: 'Change your plan',
  }[state];

  const membersLoseAccess = (when) =>
    otherMembers
      ? `Your ${plural(otherMembers, 'household member')} will lose access to shared features ${when}.`
      : undefined;

  const cardFor = ({ plan, kind }) => {
    const key = `${plan}-${kind}-${interval}`;
    const name = PLAN_DETAILS[plan].name;
    const common = {
      plan,
      interval,
      context: 'billing',
      headingLevel: 3,
      busy: busyKey === key,
    };

    if (kind === 'subscribe') {
      const hint = {
        trial: trialEndText ? `You won't be charged until your trial ends on ${trialEndText}.` : "You won't be charged until your trial ends.",
        expired: 'Planning unlocks as soon as payment goes through.',
        none: 'Secure checkout with Stripe.',
      }[state];
      return (
        <PlanCard
          key={key}
          {...common}
          actionLabel={`Subscribe to ${name}`}
          onAction={() => runAction(key, () => startCheckout({ plan, interval, returnTo: 'plans' }))}
          hint={hint}
          notice={state === 'trial' && plan === 'solo' ? membersLoseAccess('when your trial ends') : undefined}
        />
      );
    }
    if (kind === 'trial-change') {
      const price = formatPrice(getPlanPrice(plan, interval));
      return (
        <PlanCard
          key={key}
          {...common}
          actionLabel={plan === currentPlan ? `Switch to ${interval}` : `Switch to ${name}`}
          actionVariant="secondary"
          onAction={() => runAction(key, () => switchTrialPlan(plan, interval))}
          hint={`Free until your trial ends${trialEndText ? ` on ${trialEndText}` : ''}. Then ${price} per ${perInterval(interval)}.`}
          notice={plan === 'solo' ? membersLoseAccess('when your trial ends') : undefined}
        />
      );
    }
    if (kind === 'upgrade') {
      const sameInterval = interval === currentInterval;
      return (
        <PlanCard
          key={key}
          {...common}
          actionLabel="Upgrade to Family"
          onAction={() => {
            setActionError('');
            setSuccessMessage('');
            setUpgradeInterval(interval);
          }}
          hint={sameInterval && interval === 'monthly'
            ? "Starts today. You'll only pay the difference for this month."
            : "Starts today. What's left of your current plan is credited, so you only pay the difference."}
          status={statusKey === key ? 'Plan changes coming soon.' : ''}
        />
      );
    }
    if (kind === 'switch-interval') {
      return (
        <PlanCard
          key={key}
          {...common}
          actionLabel={`Switch to ${interval}`}
          actionVariant="secondary"
          onAction={() => runAction(key, () => changePlan({ plan, interval }))}
          hint={`Takes effect at your next renewal${onRenewal}. Until then you stay on ${currentInterval} billing.`}
          status={statusKey === key ? 'Plan changes coming soon.' : ''}
        />
      );
    }
    // downgrade
    return (
      <PlanCard
        key={key}
        {...common}
        actionLabel="Switch to Solo"
        actionVariant="secondary"
        onAction={() => runAction(key, () => changePlan({ plan, interval }))}
        notice={membersLoseAccess('when this takes effect')}
        hint={`Takes effect at your next renewal${onRenewal}. Until then you keep Family.`}
        status={statusKey === key ? 'Plan changes coming soon.' : ''}
      />
    );
  };

  const isPaid = state === 'solo' || state === 'family';
  // Decision C: explain why Family monthly isn't offered from yearly Solo.
  const upgradeBlockedNote = state === 'solo' && !cancelled && currentInterval === 'yearly' && interval === 'monthly' ? (
    <p className="mt-6 rounded-2xl bg-[var(--plan-notice-bg)] p-4 text-base leading-7 text-[color:var(--plan-notice-text)]">
      Family monthly isn&apos;t available as an upgrade from a yearly plan. Choose Family yearly to upgrade today.
      You can then switch to monthly when your plan renews{renewalText ? ` on ${renewalText}` : ''}.
    </p>
  ) : null;
  // Anyone with a subscription to manage, including one that starts when the trial ends.
  const hasSubscription = isPaid || state === 'trial-subscribed';

  return (
    <div className="mpg-focus-scope min-h-screen bg-[var(--plan-page-bg)]">
      {header}
      <main className="mx-auto max-w-3xl px-4 py-6">
        <PlanErrorSummary message={actionError} />
        <div role="status">
          {successMessage && (
            <p className="mb-6 rounded-2xl border border-[#BFDCCB] bg-[#EAF6EE] px-5 py-4 text-base text-[#1F5D38]">
              {successMessage}
            </p>
          )}
          {showCancelled && (
            <p className="mb-6 rounded-2xl border border-[color:var(--plan-card-border)] bg-white px-5 py-4 text-base text-[color:var(--plan-heading-text)]">
              Checkout was cancelled, so nothing was charged.
            </p>
          )}
        </div>

        {yourPlan}

        {cancelled && (
          <p className="mt-6 text-base text-[color:var(--plan-heading-text)]">
            To change your plan, keep it first.
          </p>
        )}

        {options.length > 0 && (
          <section aria-labelledby={optionsHeadingId} className="mt-10">
            <h2 id={optionsHeadingId} className="text-2xl font-bold text-[color:var(--plan-heading-text)]">
              {optionsHeading}
            </h2>
            <div className="mt-5">
              <BillingToggle value={interval} onChange={changeInterval} />
            </div>
            <div className="mt-6 flex flex-wrap gap-6">{options.map(cardFor)}</div>
          </section>
        )}
        {upgradeBlockedNote}

        <div className="mt-8 flex flex-col items-center gap-2 text-center">
          {state === 'expired' && (
            <LinkButton
              onClick={() => {
                // Same as the trial-expired screen's "continue in read-only".
                markTrialInterstitialAcknowledged(profile);
                navigate('/MealPlan', { replace: true });
              }}
            >
              Not now, continue in read-only
            </LinkButton>
          )}
          {hasSubscription && !cancelled && (
            <LinkButton tone="dark" onClick={() => runAction('cancel', () => openBillingPortal({ flow: 'cancel' }))}>
              Cancel subscription
            </LinkButton>
          )}
          {options.length > 0 && (
            <p className="mt-2 text-sm text-[color:var(--plan-muted-text)]">Prices in AUD. Secure checkout with Stripe.</p>
          )}
        </div>
      </main>
      <UpgradeConfirmDrawer
        isOpen={!!upgradeInterval}
        interval={upgradeInterval || 'monthly'}
        onClose={() => setUpgradeInterval(null)}
        onUpgraded={finishUpgrade}
      />
    </div>
  );
}
